import { assertNever } from "domains/commons/assertNever";
import { Job } from "domains/jobs/types";
import { GetAssetsByAssetIdApiResponse } from "infra/api/generated/api";

import * as Sentry from "@sentry/nextjs";

export function getAssetNumberFromJob(job: Job): number {
  switch (job.jobType) {
    case "skybox-base-360":
      return (job.metadata.input as any)?.numOutputs ?? 0;
    case "skybox-upscale-360":
    case "upscale":
    case "upscale-texture":
    case "restyle":
    case "vectorize":
    case "pixelate":
    case "remove-background":
    case "reframe":
    case "repaint":
    case "detection":
    case "upload":
    case "canvas-export":
      return 1;
    case "inference":
    case "flux":
    case "openai-image-generation":
    case "image-prompt-editing":
      return (
        (
          (job.metadata.input as any) ??
          ((job.metadata as any).parameters as any)
        ).numSamples ?? 1
      );
    case "texture":
      return 8;
    case "flux-model-training": {
      Sentry.captureException(
        "Thoses job types are not supposed to have an asset number https://github.com/scenario-labs/fawkes/pull/2861#discussion_r1819071941",
        {
          extra: {
            jobType: job.jobType,
            job,
          },
        }
      );
      return 0;
    }
    case "custom":
      return Math.max(1, job.metadata.assetIds?.length ?? 1);
    case "assets-download":
    case "caption":
    case "caption-llava":
    case "describe-style":
    case "embed":
    case "generate-prompt":
    case "model-download":
    case "model-import":
    case "model-training":
    case "patch-image":
    case "segment":
    case "translate":
    case "upscale-skybox":
    case "image-generation":
      return 0;
    default:
      return assertNever(job.jobType);
  }
}

export function getSizeFromJob(job: Job) {
  switch (job.jobType) {
    case "skybox-base-360":
    case "skybox-upscale-360":
      return {
        width: job.frontData?.width ?? 1_024,
        height: job.frontData?.height ?? 512,
      };
    case "upscale":
    case "upscale-texture":
    case "vectorize":
    case "pixelate":
    case "repaint":
    case "reframe":
    case "restyle":
    case "image-prompt-editing":
    case "detection":
    case "upload":
    case "canvas-export":
      return {
        width: job.frontData?.width ?? 512,
        height: job.frontData?.height ?? 512,
      };
    case "remove-background":
    case "inference":
    case "flux":
    case "openai-image-generation":
      return {
        width: (job.metadata.input as any).width ?? 512,
        height: (job.metadata.input as any).height ?? 512,
      };
    case "flux-model-training": {
      Sentry.captureException(
        "Thoses job types are not supposed to have a size https://github.com/scenario-labs/fawkes/pull/2861#discussion_r1819071941",
        {
          extra: {
            jobType: job.jobType,
            job,
          },
        }
      );
      return {
        width: 512,
        height: 512,
      };
    }
    case "texture":
      return {
        width: (job.metadata.input as any).textureWidth ?? 512,
        height: (job.metadata.input as any).textureHeight ?? 512,
      };
    case "assets-download":
    case "caption":
    case "caption-llava":
    case "describe-style":
    case "embed":
    case "generate-prompt":
    case "model-download":
    case "model-import":
    case "model-training":
    case "patch-image":
    case "segment":
    case "translate":
    case "upscale-skybox":
    case "image-generation":
    case "custom":
      return {
        width: 512,
        height: 512,
      };
    default:
      return assertNever(job.jobType);
  }
}

export function getTypeLabelFromJob({
  job,
  asset,
}: {
  job: Job;
  asset?: GetAssetsByAssetIdApiResponse["asset"];
}) {
  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (job.jobType) {
    case "inference":
    case "flux":
    case "openai-image-generation":
      if (((job.metadata.input as any)?.type ?? "").includes("texture")) {
        return "Texture";
      } else {
        return "Generate";
      }
    case "remove-background":
      return "Remove Background";
    case "pixelate":
      return "Pixelate";
    case "upscale":
      return "Enhance";
    case "vectorize":
      return "Vectorize";
    case "restyle":
      return "Restyle";
    case "image-prompt-editing":
      return "Edit with Prompts";
    case "reframe":
      return "Expand";

    case "upload":
      return "Upload";
    case "detection":
      return "Mode Map";
    case "canvas-export":
      return "Canvas Export";

    case "skybox-base-360":
      return "Preview";
    case "skybox-upscale-360":
      return "Enhanced Skybox";

    case "texture":
      return "Texture Set";
    case "upscale-texture":
      return "Enhanced Texture";

    case "assets-download":
      return "Download";
    case "caption":
      return "Caption";
    case "caption-llava":
      return "Caption LLava";
    case "describe-style":
      return "Describe Style";
    case "embed":
      return "Embed";
    case "generate-prompt":
      return "Generate Prompt";
    case "model-download":
      return "Model Download";
    case "model-import":
      return "Model Import";
    case "model-training":
      return "Model Training";
    case "patch-image":
      return "Patch Image";
    case "segment":
      return "Segment";
    case "translate":
      return "Translate";
    case "upscale-skybox":
      return "Upscale Skybox";
    case "custom":
      if (["img23d", "txt23d"].includes(asset?.metadata.type ?? "")) {
        return "3D";
      }
      if (
        ["img2video", "video2video", "txt2video"].includes(
          asset?.metadata.type ?? ""
        )
      ) {
        return "Video";
      }
      return "Custom";
    case "flux-model-training":
      return "Flux Model Training";
    case "repaint":
      return "Repaint";
    case "image-generation":
      return "Image Generation";
    default:
      return assertNever(job.jobType);
  }
}
